import React, { useRef, useState } from "react";
import { useIntl } from "react-intl";
import Loader from "./Loader";
import { BRAND_COLOR } from "../constants";

// Detect iOS platform
const isIOS = (): boolean => {
	return (
		typeof window !== "undefined" &&
		(/iPad|iPhone|iPod/.test(navigator.userAgent) ||
			(navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1))
	);
};

type Props = {
	loading?: boolean;
	handlePhotoSelect: (file?: File) => void;
};

/**
 * Camera/Gallery picker is rendered based on the platform.
 * - On iOS, it directly opens the native file picker.
 * - On Android, it shows a custom modal to choose between camera and gallery.
 * @returns
 */
export default function GridAddButton({ loading, handlePhotoSelect }: Props) {
	const intl = useIntl();
	const [showModal, setShowModal] = useState(false);
	const galleryInputRef = useRef<HTMLInputElement>(null);
	const cameraInputRef = useRef<HTMLInputElement>(null);

	const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			handlePhotoSelect(file);
		}
		setShowModal(false);
	};

	const openPicker = () => {
		if (isIOS()) {
			// Directly open native file picker on iOS
			galleryInputRef.current?.click();
		} else {
			// Show custom modal on Android
			setShowModal(true);
		}
	};

	return (
		<>
			<div className="col-4 p-1">
				<div
					className="card d-flex justify-content-center align-items-center"
					style={{ height: 120, cursor: "pointer" }}
					onClick={openPicker}
				>
					{loading ? (
						<Loader message="Uploading photo..." />
					) : (
						<div className="text-center">
							<i className="fas fa-plus"></i>
							<div>{intl.formatMessage({ id: "add_photo" })}</div>
						</div>
					)}
				</div>

				{/* Hidden Inputs */}
				<input
					ref={galleryInputRef}
					disabled={loading}
					type="file"
					accept="image/*"
					className="d-none"
					onChange={handleFileSelect}
				/>
				<input
					ref={cameraInputRef}
					disabled={loading}
					type="file"
					accept="image/*"
					capture="environment"
					className="d-none"
					onChange={handleFileSelect}
				/>
			</div>

			{/* Modal for Android Picker */}
			{!isIOS() && (
				<div
					className={`modal fade ${showModal ? "show d-block" : ""}`}
					tabIndex={-1}
					style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
					onClick={() => setShowModal(false)}
				>
					<div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
						<div className="modal-content">
							<div className="modal-header">
								<h5 className="modal-title">{intl.formatMessage({ id: "select_photo_source" })}</h5>
								<button type="button" className="btn-close" onClick={() => setShowModal(false)} />
							</div>
							<div className="modal-body">
								<button
									style={{ backgroundColor: BRAND_COLOR }}
									className="btn text-light w-100 mb-2"
									onClick={() => cameraInputRef.current?.click()}
								>
									{intl.formatMessage({ id: "take_photo" })}
								</button>
								<button
									style={{ backgroundColor: BRAND_COLOR }}
									className="btn text-light w-100"
									onClick={() => galleryInputRef.current?.click()}
								>
									{intl.formatMessage({ id: "choose_from_gallery" })}
								</button>
							</div>
						</div>
					</div>
				</div>
			)}
		</>
	);
}
